import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useFormStepsContext } from '@/context/FormStepsContext'
import {
  Grid,
  Autocomplete,
  TextField,
  Box,
  Button,
  IconButton,
  Typography,
  FormControl,
  styled,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Tooltip,
  FormControlProps,
  ButtonBaseProps,
  CircularProgress,
} from '@mui/material'
import { Delete } from '@mui/icons-material'
import { StepperType } from '@/utilities/types/ticket'
import PdfIcon from '@/assets/icons/pdf-icon.png'
import Mp4Icon from '@/assets/icons/mp4-icon.png'
import ImageIcon from '@/assets/icons/image-icon.png'
import theme from '@/utilities/Theme'
import { getTypeOfConcernOptions } from '@/constants'
import {
  fetchTicketAttachments,
  removeFileTicket,
  uploadTicketAttachment,
} from '@/api/maintenance-tickets-api'
import useSWR from 'swr'
import { useOpenAttachment } from '@/utilities'

const CustomLabel = styled(Typography)<{ required?: boolean }>(
  ({ required }) => ({
    color: '#505470',
    fontWeight: 500,
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',

    ...(required && {
      '&::after': {
        content: '" *"',
        color: 'red',
        fontWeight: 700,
        marginLeft: '5px',
      },
    }),
  }),
)

const CustomField = styled(TextField)({
  maxHeight: 120,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#8E8E8E',
    borderWidth: '2px',
    borderRadius: '7px',
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#ADA9BB',
    opacity: 1,
    fontWeight: 400,
  },
})

const CustomGrid = styled(Grid)({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  marginLeft: '5%',
  marginRight: '5%',
  justifyContent: 'space-between',
  // marginTop: 3,
})

const CustomFormControl = styled(FormControl)<FormControlProps>(
  ({ theme }) => ({
    width: '45%',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  }),
)

const CustomButton = styled(Button)<ButtonBaseProps>(({ theme }) => ({
  width: '65%',
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}))

type AlertSeverity = 'success' | 'error' | 'warning' | 'info'

interface FormFirstStepProps {
  formData: any
  setFormData: (data: any) => void
  type: StepperType
  data?: any
  show: boolean
  errors: Record<string, string>
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>
  handleAlertMessage: (message: string, severity: AlertSeverity) => void
}

export const FormFirstStep: FC<FormFirstStepProps> = ({
  formData,
  setFormData,
  type,
  data,
  show,
  errors,
  setErrors,
  handleAlertMessage,
}) => {
  const {
    dealers: dealersList,
    dealersLoading: loading,
    branchDealers,
    role,
    branchManager,
  } = useFormStepsContext()

  const getAllTicketQuotationAttachments = async () => {
    try {
      const response = await fetchTicketAttachments(data.id, 'QUOTATION')
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch attachments:', error)
      throw error
    }
  }

  const getAllTicketConcernAttachments = async () => {
    try {
      const response = await fetchTicketAttachments(data.id, 'CONCERN')
      console.log('concern response here ', response)
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch attachments:', error)
      throw error
    }
  }

  const shouldFetchAttachments = type === 'Update'

  const { data: concernAttachments, mutate: mutateConcernAttachments } = useSWR(
    shouldFetchAttachments ? `/v1/attachment/concern/${data.id}` : null,
    getAllTicketConcernAttachments,
    { revalidateOnFocus: false },
  )

  const { data: quotationAttachments, mutate: mutateQuotationAttachments } =
    useSWR(
      shouldFetchAttachments ? `/v1/attachment/quotation/${data.id}` : null,
      getAllTicketQuotationAttachments,
      { revalidateOnFocus: false },
    )

  const [concernLoading, setConcernLoading] = useState(false)
  const [quotationLoading, setQuotationLoading] = useState(false)

  const openAttachment = useOpenAttachment()

  const handleUploadConcern = async (
    e: React.ChangeEvent<HTMLInputElement>,
    file: File,
  ) => {
    const maxSize = 20 * 1024 * 1024

    if (file.size > maxSize) {
      handleAlertMessage(`File should not exceed 20mb.`, 'error')
      return
    }

    try {
      setConcernLoading(true)
      await uploadTicketAttachment(data.id, 'CONCERN', file)
      mutateConcernAttachments()
    } catch (error) {
      console.error('Failed to upload dis attachment:', error)
    } finally {
      setConcernLoading(false)
      e.target.value = ''
    }
  }

  const handleUploadQuotation = async (
    e: React.ChangeEvent<HTMLInputElement>,
    file: File,
  ) => {
    const maxSize = 20 * 1024 * 1024

    if (file.size > maxSize) {
      handleAlertMessage(`File should not exceed 20mb.`, 'error')
      return
    }

    try {
      setQuotationLoading(true)
      await uploadTicketAttachment(data.id, 'QUOTATION', file)
      mutateQuotationAttachments()
    } catch (error) {
      console.error('Failed to upload dis attachment:', error)
    } finally {
      setQuotationLoading(false)
      e.target.value = ''
    }
  }

  const handleRemoveConcern = async (idToRemove: number) => {
    if (!concernAttachments) return
    const remainingIds = concernAttachments
      .filter((file: any) => file.id === idToRemove)
      .map((file: any) => file.id)
    try {
      await removeFileTicket(data.id, remainingIds)
      mutateConcernAttachments()
    } catch (error) {
      console.error('Failed to remove file:', error)
    }
  }

  const handleRemoveQuotation = async (idToRemove: number) => {
    if (!quotationAttachments) return
    const remainingIds = quotationAttachments
      .filter((file: any) => file.id === idToRemove)
      .map((file: any) => file.id)
    try {
      await removeFileTicket(data.id, remainingIds)
      mutateQuotationAttachments()
    } catch (error) {
      console.error('Failed to remove file:', error)
    }
  }

  const typeOfConcerns = [
    'Appliance',
    'Building',
    'Collateral',
    'Exterior',
    'Interior',
  ]
  const repairMethods = ['C/O Dealer', 'C/O YMPH']

  const validatedDealers = useMemo(() => dealersList || [], [dealersList])
  const validatedBranchDealers = useMemo(
    () => branchDealers || [],
    [branchDealers],
  )

  const costSharingOptions = useMemo(() => {
    return formData.selectedRepairMethod === 'C/O Dealer'
      ? ['100% YMPH', '100% Dealer', '50-50 YMPH & Dealer']
      : ['100% YMPH', '100% Dealer', '50-50 YMPH & Dealer']
  }, [formData.selectedRepairMethod])

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string,
  ) => {
    const files = event.target.files
    if (!files) return

    const maxSize = 20 * 1024 * 1024

    const validFiles: File[] = []
    for (const file of Array.from(files)) {
      if (file.size > maxSize) {
        handleAlertMessage(`File should not exceed 20mb.`, 'error')
        return
      }

      if (
        file.type.startsWith('image/') ||
        file.type === 'application/pdf' ||
        file.type === 'video/mp4'
      ) {
        validFiles.push(file)
      }
    }

    const newFiles = Array.from(files).filter(
      (file) =>
        file.type.startsWith('image/') ||
        file.type === 'application/pdf' ||
        file.type === 'video/mp4',
    )
    if (type === 'Concern') {
      setFormData({
        ...formData,
        concernAttachments: [
          ...(formData.concernAttachments || []),
          ...newFiles,
        ],
      })
    } else {
      setFormData({
        ...formData,
        quotationAttachments: [
          ...(formData.quotationAttachments || []),
          ...newFiles,
        ],
      })
    }
    event.target.value = ''
  }

  const handleRemoveFile = (index: number, type: string, event: any) => {
    if (type === 'Concern') {
      const updatedFiles = formData.concernAttachments.filter(
        (_: any, i: number) => i !== index,
      )
      setFormData({ ...formData, concernAttachments: updatedFiles })
    } else {
      const updatedFiles = formData.quotationAttachments.filter(
        (_: any, i: number) => i !== index,
      )
      setFormData({ ...formData, quotationAttachments: updatedFiles })
    }
    event.target.value = ''
  }

  useEffect(() => {
    if (
      (role === 'Branch Manager' || role === 'OIC') &&
      branchManager &&
      type === 'Create'
    ) {
      console.log('DATA', formData)
      setFormData((prev: any) => ({
        ...prev,
        selectedDealer: branchManager.dealerName,
        shop: branchManager.dealerBranch,
        selectedBranchDealer: {
          branchName: branchManager.branchName,
          provinceName: branchManager.provinceName,
          shop: branchManager.dealerBranch,
        },
        requestorName: branchManager.branchManagerName,
        area: branchManager.areaName,
        province: branchManager.provinceName,
        branchId: branchManager.branchId,
        purpose: 'Request',
        costAmount: branchManager?.costAmount ?? formData?.costAmount,
      }))
    } else if (
      (role === 'Branch Manager' || role === 'OIC') &&
      type === 'Update' &&
      data
    ) {
      console.log('DATA', data)
      setFormData((prev: any) => ({
        ...prev,
        selectedDealer: data.dealerName,
        selectedBranchDealer: {
          branchName: data.branchName,
          provinceName: data.provinceName,
          shop: data.shop,
        },
        shop: data.shop,
        requestorName: data.requestor,
        area: data.areaName,
        province: data.provinceName,
        branchId: data.branchId,
        costAmount: data.amount.toString(),
        selectedConcern: data.typeOfConcern,
        selectedCollateral: data.typeOfCollateral,
        selectedAppliance: data.typeOfAppliance,
        typeOfConcern: data.typeOfConcern,
        typeOfConcernValue: data.typeOfConcernValue,
        typeOfConcernOthers: data.typeOfConcernOthers,
        description: data.description,
        concernAttachments: concernAttachments || [],
        quotationAttachments: quotationAttachments || [],
      }))
    }
  }, [show, role, type, data, setFormData, branchManager])

  useEffect(() => {
    if (type === 'Update' && data) {
      setFormData((prev: any) => ({
        ...prev,
        concernAttachments: concernAttachments || [],
        quotationAttachments: quotationAttachments || [],
      }))
    }
  }, [concernAttachments, quotationAttachments])

  return (
    <Box width={'100%'} height={'100%'} sx={{ overflowX: 'hidden' }}>
      {(data.status === 'REJECTED' ||
        data.status === 'FOR_REVISION' ||
        data.status === 'DENIED' ||
        data.status === 'REVISE') && (
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'row',
            marginLeft: '5%',
            marginRight: '5%',
            justifyContent: 'space-between',
            marginTop: 3,
          }}
        >
          <FormControl sx={{ width: '100%' }}>
            <CustomLabel>Remarks</CustomLabel>
            <Box sx={{ width: '100%', height: '75px', overflow: 'auto' }}>
              <Typography>{data.remark}</Typography>
            </Box>
          </FormControl>
        </Grid>
      )}

      <CustomGrid>
        <CustomFormControl>
          <CustomLabel>Dealer Name</CustomLabel>
          <Autocomplete
            disablePortal
            options={validatedDealers}
            getOptionLabel={(option) => option || ''}
            value={formData.selectedDealer}
            disabled
            onChange={(_, value) =>
              setFormData({ ...formData, selectedDealer: value })
            }
            loading={loading}
            renderInput={(params) => (
              <CustomField {...params} variant="outlined" size="small" />
            )}
          />
        </CustomFormControl>

        <CustomFormControl>
          <CustomLabel>Branch Name</CustomLabel>
          <Autocomplete
            disablePortal
            options={validatedBranchDealers}
            getOptionLabel={(option) => option.branchName || ''}
            value={formData.selectedBranchDealer}
            disabled
            onChange={(_, value) =>
              setFormData({ ...formData, selectedBranchDealer: value })
            }
            renderInput={(params) => (
              <CustomField {...params} variant="outlined" size="small" />
            )}
          />
        </CustomFormControl>
      </CustomGrid>
      <CustomGrid marginTop={3}>
        <CustomFormControl>
          <CustomLabel>Requestor</CustomLabel>
          <CustomField
            value={formData.requestorName || ''}
            size="small"
            disabled
          />
        </CustomFormControl>

        <CustomFormControl>
          <CustomLabel>Shop Name</CustomLabel>
          <CustomField value={formData.shop} size="small" disabled />
        </CustomFormControl>
      </CustomGrid>

      <CustomGrid marginTop={3}>
        <CustomFormControl>
          <CustomLabel>Area</CustomLabel>
          <CustomField
            value={formData.area || ''}
            fullWidth
            disabled
            size="small"
          />
        </CustomFormControl>
        <CustomFormControl>
          <CustomLabel>Province</CustomLabel>
          <CustomField
            value={formData.selectedBranchDealer?.provinceName || ''}
            fullWidth
            disabled
            size="small"
          />
        </CustomFormControl>
      </CustomGrid>

      <CustomGrid marginTop={3}>
        <CustomFormControl>
          <CustomLabel required>Type of Concern</CustomLabel>
          <Autocomplete
            disablePortal
            options={typeOfConcerns}
            value={formData.typeOfConcern}
            onChange={(_, value) => {
              setFormData({
                ...formData,
                typeOfConcern: value,
                typeOfConcernValue: '',
              })
              setErrors((prevErrors) => ({
                ...prevErrors,
                typeOfConcern: '',
              }))
            }}
            renderInput={(params) => (
              <CustomField {...params} variant="outlined" size="small" />
            )}
          />
          {errors.typeOfConcern && (
            <FormHelperText sx={{ color: 'red' }}>
              {errors.typeOfConcern}
            </FormHelperText>
          )}
        </CustomFormControl>
        {formData.typeOfConcern && (
          <CustomFormControl>
            <CustomLabel
              required
            >{`Type of ${formData.typeOfConcern}`}</CustomLabel>
            <Autocomplete
              disablePortal
              options={getTypeOfConcernOptions(formData.typeOfConcern)}
              value={formData.typeOfConcernValue}
              onChange={(_, value) => {
                setFormData({
                  ...formData,
                  typeOfConcernValue: value,
                  typeOfConcernOthers: '',
                })
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  typeOfConcernValue: '',
                  typeOfConcernOthers: '',
                }))
              }}
              renderInput={(params) => (
                <CustomField {...params} variant="outlined" size="small" />
              )}
            />
            {errors.typeOfConcernValue && (
              <FormHelperText sx={{ color: 'red' }}>
                {errors.typeOfConcernValue}
              </FormHelperText>
            )}

            {formData.typeOfConcern &&
              formData.typeOfConcernValue === 'Others' && (
                <>
                  <CustomLabel required marginTop={1}>
                    Others
                  </CustomLabel>
                  <CustomField
                    value={formData.typeOfConcernOthers}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        typeOfConcernOthers: e.target.value,
                      })
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        typeOfConcernOthers: '',
                      }))
                    }}
                    fullWidth
                    size="small"
                  />

                  {errors.typeOfConcernOthers && (
                    <FormHelperText sx={{ color: 'red' }}>
                      {errors.typeOfConcernOthers}
                    </FormHelperText>
                  )}
                </>
              )}
          </CustomFormControl>
        )}
      </CustomGrid>
      {formData.purpose && (
        <CustomGrid marginTop={3}>
          <CustomFormControl component="fieldset">
            <CustomLabel required>Method of Repair</CustomLabel>
            <RadioGroup
              row
              value={formData.selectedRepairMethod || ''}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  selectedRepairMethod: e.target.value,
                })
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  selectedRepairMethod: '',
                }))
              }}
            >
              {repairMethods.map((method) => {
                const [prefix, suffix] = method.split('C/O ')
                const label = method.includes('C/O ') ? `c/o ${suffix}` : method

                return (
                  <FormControlLabel
                    key={method}
                    value={method}
                    control={<Radio />}
                    label={label}
                  />
                )
              })}
            </RadioGroup>
            {errors.selectedRepairMethod && (
              <FormHelperText sx={{ color: 'red' }}>
                {errors.selectedRepairMethod}
              </FormHelperText>
            )}
          </CustomFormControl>
          {formData.selectedRepairMethod && (
            <CustomFormControl>
              <CustomLabel required>Cost Sharing</CustomLabel>
              <RadioGroup
                row
                value={formData.costOfSharing}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    costOfSharing: e.target.value,
                  })
                  setErrors((prevErrors) => ({
                    ...prevErrors,
                    costOfSharing: '',
                  }))
                }}
              >
                {costSharingOptions.map((option) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio />}
                    label={option}
                  />
                ))}
              </RadioGroup>
              {errors.costOfSharing && (
                <FormHelperText sx={{ color: 'red' }}>
                  {errors.costOfSharing}
                </FormHelperText>
              )}
            </CustomFormControl>
          )}
        </CustomGrid>
      )}

      <CustomGrid marginTop={3}>
        <CustomFormControl>
          <CustomLabel>Cost Amount</CustomLabel>
          <CustomField
            fullWidth
            variant="outlined"
            type="text"
            value={formData.costAmount}
            size="small"
            onChange={(e) => {
              const rawInput = e.target.value.replace(/[^0-9.]/g, '')
              if (/^\d*\.?\d{0,3}$/.test(rawInput) || rawInput === '') {
                setFormData({
                  ...formData,
                  costAmount: rawInput,
                })
              }
              setErrors((prevErrors) => ({
                ...prevErrors,
                costAmount: '',
              }))
            }}
            onBlur={() => {
              const num = parseFloat(formData.costAmount.replace(/,/g, '')) || 0

              const formatted = new Intl.NumberFormat(undefined, {
                minimumFractionDigits: 3,
                maximumFractionDigits: 3,
              }).format(num)

              setFormData({
                ...formData,
                costAmount: formatted,
              })
            }}
          />
          {/* {errors.costAmount && (
            <FormHelperText sx={{ color: 'red' }}>
              {errors.costAmount}
            </FormHelperText>
          )} */}
        </CustomFormControl>
      </CustomGrid>

      <CustomGrid marginTop={3}>
        <FormControl sx={{ width: '100%' }}>
          <CustomLabel required>Description</CustomLabel>
          <CustomField
            fullWidth
            variant="outlined"
            placeholder="Cause of Damage..."
            value={formData.description}
            size="small"
            multiline
            minRows={3}
            onChange={(e) => {
              setFormData({ ...formData, description: e.target.value })
              setErrors((prevErrors) => ({
                ...prevErrors,
                description: '',
              }))
            }}
          />
          {errors.description && (
            <FormHelperText sx={{ color: 'red' }}>
              {errors.description}
            </FormHelperText>
          )}
        </FormControl>
      </CustomGrid>

      <CustomGrid sx={{ marginTop: 2 }}>
        <Grid
          sx={{
            width: '45%',
            [theme.breakpoints.down('md')]: {
              width: '100%',
            },
          }}
        >
          <FormControl
            fullWidth
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
            }}
          >
            <Typography>
              Attach Concern Attachment(s)
              <Typography component="span" color="red" ml={0.5}>
                *
              </Typography>
            </Typography>
            <CustomButton
              variant="contained"
              component="label"
              sx={{ width: '60%' }}
            >
              {type === 'Create' && (
                <>
                  <Box display="flex" alignItems="center">
                    Attach
                  </Box>
                  <input
                    type="file"
                    hidden
                    multiple
                    ref={fileInputRef}
                    accept="image/*,application/pdf,video/mp4"
                    onChange={(e) => {
                      handleFileUpload(e, 'Concern')
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        concernAttachments: '',
                      }))
                    }}
                  />
                </>
              )}

              {type === 'Update' && (
                <>
                  {concernLoading ? (
                    <Box display={'flex'} alignItems={'center'}>
                      <CircularProgress size={25} color="success" />
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center">
                      Attach
                    </Box>
                  )}
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*,application/pdf,video/mp4"
                    onChange={(e) => {
                      const files = e.target.files
                      if (files?.length) {
                        const firstFile = files[0]
                        handleUploadConcern(e, firstFile)
                      }
                    }}
                  />
                </>
              )}
            </CustomButton>
            <Typography
              fontSize={'10px'}
              marginTop={'5px'}
              sx={{
                color: 'gray',
                width: '60%',
                textAlign: 'center',
                [theme.breakpoints.down('md')]: {
                  width: '100%',
                },
              }}
            >
              (JPEG,PDF,MP4 - 20MB)
            </Typography>
            {errors.concernAttachments && (
              <FormHelperText sx={{ color: 'red' }}>
                {errors.concernAttachments}
              </FormHelperText>
            )}
          </FormControl>

          <Box display="flex" flexDirection="column" gap={4} mt={2}>

          {formData.concernAttachments?.length > 0 && type === 'Create' && (
            <>
              {formData.concernAttachments
                .sort((a: any, b: any) => a.id - b.id)
                .map(
                  (
                    file:
                      | File
                      | { fileType: string; fileName: string; url: string },
                    index: number,
                  ) => {
                    const isFileInstance = file instanceof File
                    const fileType = isFileInstance ? file.type : file.fileType
                    const fileName = isFileInstance ? file.name : file.fileName
                    const fileUrl = isFileInstance
                      ? URL.createObjectURL(file)
                      : file.url

                    return (
                      <Grid
                        container
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                        }}
                        gap={1}
                      >
                        {fileType?.startsWith('image/') ? (
                          <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            width={'100%'}
                            height={'25px'}
                          >
                            <Grid
                              display={'flex'}
                              flexDirection={'row'}
                              alignItems={'center'}
                              gap={1}
                            >
                            <a target="_blank" href={fileUrl} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
                              <img
                                src={ImageIcon}
                                alt="Preview"
                                width={25}
                                height={25}
                                style={{ objectFit: 'cover' }}
                              />
                              <Tooltip title={fileName} arrow>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: '120px',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer',
                                    ml: 1,
                                  }}
                                >

                                    {fileName}
                                </Typography>

                              </Tooltip>
                              </a> 
                              <IconButton
                                onClick={(event) =>
                                  handleRemoveFile(index, 'Concern', event)
                                }
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </Grid>
                          </Box>
                        ) : fileType === 'application/pdf' ? (
                          <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            width={'100%'}
                            height={'25px'}
                          >
                            <Grid
                              display={'flex'}
                              flexDirection={'row'}
                              alignItems={'center'}
                              gap={1}
                            >
                            <a target="_blank" href={fileUrl} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
                              <img
                                src={PdfIcon}
                                alt="PDF"
                                width={25}
                                height={25}
                                style={{ objectFit: 'cover' }}
                              />
                              <Tooltip title={fileName} arrow>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: '120px',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer',
                                    ml: 1,
                                  }}
                                >
                                    {fileName}
                                </Typography>
                              </Tooltip>
                              </a>
                              <IconButton
                                onClick={(event) =>
                                  handleRemoveFile(index, 'Concern', event)
                                }
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </Grid>
                          </Box>
                        ) : fileType === 'video/mp4' ? (
                          <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            width={'100%'}
                            height={'25px'}
                          >
                            <Grid
                              display={'flex'}
                              flexDirection={'row'}
                              alignItems={'center'}
                              gap={1}
                            >
                                <a target="_blank" href={fileUrl} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
                              <img
                                src={Mp4Icon}
                                alt="MP4"
                                width={25}
                                height={25}
                                style={{ objectFit: 'cover' }}
                              />
                              <Tooltip title={fileName} arrow>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: '120px',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer',
                                  }}
                                >
                                    {fileName}
                                </Typography>
                              </Tooltip>
                              </a>
                              <IconButton
                                onClick={(event) =>
                                  handleRemoveFile(index, 'Concern', event)
                                }
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </Grid>
                          </Box>
                        ) : null}
                      </Grid>
                    )
                  },
                )}
            </>
          )}
          </Box>


          <Box display="flex" flexDirection="column" gap={4} mt={2}>
          {concernAttachments?.length > 0 && type === 'Update' && (
            <>
              {concernAttachments
                .sort((a: any, b: any) => a.id - b.id)
                .map(
                  (
                    file:
                      | File
                      | { fileType: string; fileName: string; url: string },
                    index: number,
                  ) => {
                    const isFileInstance = file instanceof File
                    const fileType = isFileInstance ? file.type : file.fileType
                    const fileName = isFileInstance ? file.name : file.fileName
                    const fileUrl = isFileInstance
                      ? URL.createObjectURL(file)
                      : file.url

                    return (
                      <Grid
                        container
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                        }}
                      >
                        {fileType?.startsWith('image/') ? (
                          <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            width={'100%'}
                            height={'25px'}
                          >
                            <Grid
                              display={'flex'}
                              flexDirection={'row'}
                              alignItems={'center'}
                              gap={1}
                            >
                              <Tooltip
                                title={fileName}
                                arrow
                                onClick={() => openAttachment(fileName)}
                              >
  <a target="_blank" href={fileUrl} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>

                                <img
                                  src={ImageIcon}
                                  alt="Preview"
                                  width={25}
                                  height={25}
                                  style={{ objectFit: 'cover' }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: '120px',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer',
                                    ml: 1,
                                  }}
                                >
                                    {fileName}
                                </Typography>
                                </a> 
                              </Tooltip>

                              <IconButton
                                onClick={() =>
                                  handleRemoveConcern((file as any).id)
                                }
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </Grid>
                          </Box>
                        ) : fileType === 'application/pdf' ? (
                          <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            width={'100%'}
                            height={'25px'}
                          >
                            <Grid
                              display={'flex'}
                              flexDirection={'row'}
                              alignItems={'center'}
                              gap={1}
                            >
  <a target="_blank" href={fileUrl} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>

                              <Tooltip
                                title={fileName}
                                arrow
                                onClick={() => openAttachment(fileName)}
                              >
                                <img
                                  src={PdfIcon}
                                  alt="PDF"
                                  width={25}
                                  height={25}
                                  style={{ objectFit: 'cover' }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: '120px',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer',
                                    ml: 1,
                                  }}
                                >
                                    {fileName}
                                </Typography>
                              </Tooltip>
                              </a>
                              <IconButton
                                onClick={() =>
                                  handleRemoveConcern((file as any).id)
                                }
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </Grid>
                          </Box>
                        ) : fileType === 'video/mp4' ? (
                          <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            width={'100%'}
                            height={'25px'}
                          >
                            <Grid
                              display={'flex'}
                              flexDirection={'row'}
                              alignItems={'center'}
                              gap={1}
                            >
  <a target="_blank" href={fileUrl} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>

                              <img
                                src={Mp4Icon}
                                alt="MP4"
                                width={25}
                                height={25}
                                style={{ objectFit: 'cover' }}
                              />
                              <Tooltip
                                title={fileName}
                                arrow
                                onClick={() => openAttachment(fileName)}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: '120px',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer',
                                  }}
                                >
                                    {fileName}
                                </Typography>
                              </Tooltip>
                              </a> 
                              <IconButton
                                onClick={() =>
                                  handleRemoveConcern((file as any).id)
                                }
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </Grid>
                          </Box>
                        ) : null}
                      </Grid>
                    )
                  },
                )}
            </>
          )}
          </Box>
        </Grid>


        {formData.purpose === 'Reimbursement' && (
          <Grid
            sx={{
              width: '45%',
              [theme.breakpoints.down('md')]: {
                width: '100%',
              },
            }}
          >
            <FormControl
              fullWidth
              sx={{
                width: '100%',
              }}
            >
              {type === 'Create' && (
                <Button
                  variant="contained"
                  component="label"
                  sx={{
                    width: '60%',
                    [theme.breakpoints.down('md')]: {
                      width: '100%',
                    },
                  }}
                >
                  Attach
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*,application/pdf,video/mp4"
                    onChange={(e) => handleFileUpload(e, 'Quotation')}
                  />
                </Button>
              )}

              {type === 'Update' && (
                <Button
                  variant="contained"
                  component="label"
                  sx={{
                    width: '60%',
                    [theme.breakpoints.down('md')]: {
                      width: '100%',
                    },
                  }}
                >
                  {quotationLoading ? (
                    <Box display={'flex'} alignItems={'center'}>
                      <CircularProgress size={25} color="success" />
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center">
                      Attach
                    </Box>
                  )}
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*,application/pdf,video/mp4"
                    onChange={(e) => {
                      const files = e.target.files
                      if (files?.length) {
                        const firstFile = files[0]
                        handleUploadQuotation(e, firstFile)
                      }
                    }}
                  />
                </Button>
              )}
            </FormControl>
            {formData.quotationAttachments?.length > 0 && type === 'Create' && (
              <>
                {formData.quotationAttachments.map(
                  (
                    file:
                      | File
                      | { fileType: string; fileName: string; url: string },
                    index: number,
                  ) => {
                    const isFileInstance = file instanceof File
                    const fileType = isFileInstance ? file.type : file.fileType
                    const fileName = isFileInstance ? file.name : file.fileName
                    const fileUrl = isFileInstance
                      ? URL.createObjectURL(file)
                      : file.url

                    return (
                      <Grid
                        container
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                        }}
                      >
                        {fileType?.startsWith('image/') ? (
                          <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            width={'100%'}
                            height={'25px'}
                          >
                            <Grid
                              display={'flex'}
                              flexDirection={'row'}
                              alignItems={'center'}
                              gap={1}
                            >
  <a target="_blank" href={fileUrl} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>

                              <img
                                src={ImageIcon}
                                alt="Preview"
                                width={25}
                                height={25}
                                style={{ objectFit: 'cover' }}
                              />
                              <Tooltip title={fileName} arrow>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: '120px',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer',
                                    ml: 1,
                                  }}
                                >
                                    {fileName}
                                </Typography>
                              </Tooltip>
                                </a>
                              <IconButton
                                onClick={(event) =>
                                  handleRemoveFile(index, 'Concern', event)
                                }
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </Grid>
                          </Box>
                        ) : fileType === 'application/pdf' ? (
                          <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            width={'100%'}
                            height={'25px'}
                          >
                            <Grid
                              display={'flex'}
                              flexDirection={'row'}
                              alignItems={'center'}
                              gap={1}
                            >
  <a target="_blank" href={fileUrl} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>

                              <img
                                src={PdfIcon}
                                alt="PDF"
                                width={25}
                                height={25}
                                style={{ objectFit: 'cover' }}
                              />
                              <Tooltip title={fileName} arrow>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: '120px',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer',
                                    ml: 1,
                                  }}
                                >
                                    {fileName}
                                </Typography>
                              </Tooltip>
                              </a>

                              <IconButton
                                onClick={(event) =>
                                  handleRemoveFile(index, 'Concern', event)
                                }
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </Grid>
                          </Box>
                        ) : fileType === 'video/mp4' ? (
                          <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            width={'100%'}
                            height={'25px'}
                          >
                            <Grid
                              display={'flex'}
                              flexDirection={'row'}
                              alignItems={'center'}
                              gap={1}
                            >
  <a target="_blank" href={fileUrl} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>

                              <img
                                src={Mp4Icon}
                                alt="MP4"
                                width={25}
                                height={25}
                                style={{ objectFit: 'cover' }}
                              />
                              <Tooltip title={fileName} arrow>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: '120px',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer',
                                    ml: 1,
                                  }}
                                >
                                    {fileName}
                                </Typography>
                              </Tooltip>
                              </a>
                              <IconButton
                                onClick={(event) =>
                                  handleRemoveFile(index, 'Concern', event)
                                }
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </Grid>
                          </Box>
                        ) : null}
                      </Grid>
                    )
                  },
                )}
              </>
            )}
            {quotationAttachments?.length > 0 && type === 'Update' && (
              <>
                {quotationAttachments.map(
                  (
                    file:
                      | File
                      | { fileType: string; fileName: string; url: string },
                    index: number,
                  ) => {
                    const isFileInstance = file instanceof File
                    const fileType = isFileInstance ? file.type : file.fileType
                    const fileName = isFileInstance ? file.name : file.fileName
                    const fileUrl = isFileInstance
                      ? URL.createObjectURL(file)
                      : file.url

                    return (
                      <Grid
                        container
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                        }}
                      >
                        {fileType?.startsWith('image/') ? (
                          <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            width={'100%'}
                            height={'25px'}
                          >
                            <Grid
                              display={'flex'}
                              flexDirection={'row'}
                              alignItems={'center'}
                              gap={1}
                            >
  <a target="_blank" href={fileUrl} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
                              <img
                                src={ImageIcon}
                                alt="Preview"
                                width={25}
                                height={25}
                                style={{ objectFit: 'cover' }}
                              />
                              <Tooltip
                                title={fileName}
                                arrow
                                onClick={() => openAttachment(fileName)}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: '120px',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer',
                                    ml: 1,
                                  }}
                                >
                                    {fileName}
                                </Typography>
                              </Tooltip>
                              </a>
                              <IconButton
                                onClick={() =>
                                  handleRemoveConcern((file as any).id)
                                }
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </Grid>
                          </Box>
                        ) : fileType === 'application/pdf' ? (
                          <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            width={'100%'}
                            height={'25px'}
                          >
                            <Grid
                              display={'flex'}
                              flexDirection={'row'}
                              alignItems={'center'}
                              gap={1}
                            >
                              <img
                                src={PdfIcon}
                                alt="PDF"
                                width={25}
                                height={25}
                                style={{ objectFit: 'cover' }}
                              />
                              <Tooltip
                                title={fileName}
                                arrow
                                onClick={() => openAttachment(fileName)}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: '120px',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer',
                                  }}
                                >
                                  {fileName}
                                </Typography>
                              </Tooltip>
                              <IconButton
                                onClick={() =>
                                  handleRemoveConcern((file as any).id)
                                }
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </Grid>
                          </Box>
                        ) : fileType === 'video/mp4' ? (
                          <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            width={'100%'}
                            height={'25px'}
                          >
                            <Grid
                              display={'flex'}
                              flexDirection={'row'}
                              alignItems={'center'}
                              gap={1}
                            >
  <a target="_blank" href={fileUrl} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>

                              <img
                                src={Mp4Icon}
                                alt="MP4"
                                width={25}
                                height={25}
                                style={{ objectFit: 'cover' }}
                              />
                              <Tooltip
                                title={fileName}
                                arrow
                                onClick={() => openAttachment(fileName)}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: '120px',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer',
                                    ml: 1,
                                  }}
                                >
                                    {fileName}
                                </Typography>
                              </Tooltip>
                              </a>
                              <IconButton
                                onClick={() =>
                                  handleRemoveConcern((file as any).id)
                                }
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </Grid>
                          </Box>
                        ) : null}
                      </Grid>
                    )
                  },
                )}
              </>
            )}
          </Grid>
        )}
      </CustomGrid>
    </Box>
  )
}
